import { defineStore } from 'pinia'
import type { UserProfile } from '@/stores/models/UserProfile'
import { computed, type Ref, ref } from 'vue'
import type { languages } from '@/i18n/i18n'
import {
  type DepartmentDTO,
  type HospitalDTO,
  type PluginContraindicationAcceptanceDto,
  ShiftTypeDTO,
  UserRightsEnum,
  type WardDTO
} from '@/generated'
import type { FirebaseOptions } from '@/global/FirebaseOptions'
import { useLocalStorage } from '@vueuse/core'
import { parseISO } from 'date-fns'

export const useUserStore = defineStore('user', () => {
  const isInitializing = ref(true)

  const user: Ref<UserProfile | undefined> = ref(undefined)
  const currentRoleInStorage = useLocalStorage<UserRightsEnum | undefined>('currentRole', undefined)
  const hospitalId = useLocalStorage<string | undefined>('hospitalId', undefined)

  const getTitle = computed(() => user.value!.title ?? '')
  const getFullName = computed(() =>
    [user.value!.firstName, user.value!.lastName].filter((elm) => elm !== undefined).join(' ')
  )
  const getDisplayName = computed(() => {
    const displayName = [user.value!.title, user.value!.firstName, user.value!.lastName]
      .filter((elm) => elm !== undefined)
      .join(' ')

    return displayName.length !== 0 ? displayName : user.value?.userName
  })
  const getMedicalFields = computed(() => user.value?.medicalFields ?? [])
  const getDepartments = computed(() => user.value?.departments ?? [])
  const getHospitals = computed(() => user.value?.hospitals ?? [])

  const hasSelectedShift = computed<boolean>(() => !!user.value!.currentShift)
  const hasSelectedRole = computed<boolean>(() => !!user.value!.currentRole)
  const hasSelectedShiftDuration = ref(false)
  const hadSelectedShift = ref(false)

  const isIncognito = computed<boolean>(() => user.value?.currentShift === ShiftTypeDTO.NO_SHIFT)

  const hasGodModeShift = computed<boolean>(
    () => user.value?.currentShift === ShiftTypeDTO.GOD_MODE
  )

  const hasChiefPhysicianShift = computed<boolean>(
    () => user.value?.currentShift === ShiftTypeDTO.CHIEF_PHYSICIAN
  )

  const hasHospitalItRole = computed<boolean>(
    () => user.value?.currentRole === UserRightsEnum.HOSPITAL_IT
  )

  const hasHospitalPhysicianRole = computed<boolean>(
    () => user.value?.userRights.includes(UserRightsEnum.HOSPITAL_PHYSICIAN) ?? false
  )

  const hasTccItRole = computed<boolean>(() => user.value?.currentRole === UserRightsEnum.TCC_IT)

  const hasDoctorRole = computed<boolean>(() => {
    const doctorRoles: UserRightsEnum[] = [
      UserRightsEnum.HOSPITAL_PHYSICIAN,
      UserRightsEnum.TCC_PHYSICIAN,
      UserRightsEnum.GOD_MODE,
      UserRightsEnum.CHIEF_PHYSICIAN,
      UserRightsEnum.HUB,
      UserRightsEnum.SPOKE
    ]

    return user.value?.userRights.some((right) => doctorRoles.includes(right)) ?? false
  })

  const isTheSameAsUser = (userName: string): boolean => {
    return user.value?.userName === userName
  }

  const isConsultationDisabledForPatient = (patientHospitalId?: string): boolean => {
    if (hasGodModeShift.value) {
      return true
    }

    if (!patientHospitalId) {
      return true
    }

    const userHospitalIds = getHospitals.value.map((hospital) => hospital.id)
    return !userHospitalIds.includes(patientHospitalId)
  }

  const hasWardWithoutPDMS = computed<boolean>(
    () => user.value?.wards?.some((ward) => ward.hasPDMS === false) ?? false
  )

  const notificationToken: Ref<string | undefined> = ref(undefined)
  const firebaseOptions: Ref<FirebaseOptions | undefined> = ref(undefined)

  const notificationTokenLoadingState: Ref<
    'initial' | 'loading' | 'success' | 'error' | 'deactivated'
  > = ref('initial')

  const endOfShiftDate = ref<Date | null>(null)

  function setUser(newUser: UserProfile | undefined) {
    user.value = newUser
    if (newUser && currentRoleInStorage.value) {
      newUser.currentRole = currentRoleInStorage.value
    }
  }

  function setLanguage(language: languages) {
    user.value!.language = language
  }

  function setCurrentShift(newShift: ShiftTypeDTO | undefined) {
    user.value!.currentShift = newShift
  }

  function setWards(newWards: WardDTO[] | undefined) {
    user.value!.wards = newWards
  }

  function setDepartments(newDepartments: DepartmentDTO[] | undefined) {
    user.value!.departments = newDepartments
  }

  function setHospitals(newHospitals: HospitalDTO[] | undefined) {
    user.value!.hospitals = newHospitals
  }

  function setMedicalFields(newMedicalFields: string[] | undefined) {
    user.value!.medicalFields = newMedicalFields
  }

  function setNotificationToken(token: string): void {
    notificationToken.value = token
  }

  function clearNotificationToken(): void {
    notificationToken.value = undefined
  }

  function setFirebaseOptions(options: FirebaseOptions): void {
    firebaseOptions.value = options
  }

  function setNotificationTokenLoadingState(
    state: 'initial' | 'loading' | 'success' | 'error' | 'deactivated'
  ): void {
    notificationTokenLoadingState.value = state
  }

  function setUserRights(userRights: UserRightsEnum[]): void {
    user.value!.userRights = userRights
  }

  function setCurrentRole(currentRole?: UserRightsEnum): void {
    user.value!.currentRole = currentRole
    currentRoleInStorage.value = currentRole
  }

  function setHadSelectedShift(newValue: boolean): void {
    hadSelectedShift.value = newValue
  }

  function setEndOfShiftDate(endDate: string | undefined | null): void {
    if (endDate) {
      endOfShiftDate.value = parseISO(endDate)
      hasSelectedShiftDuration.value = true
    } else {
      endOfShiftDate.value = null
      hasSelectedShiftDuration.value = false
    }
  }

  function setHospitalId(newHospitalId: string | undefined): void {
    hospitalId.value = newHospitalId
  }

  const canCreateConsultations = ref(false)
  function setCanCreateConsultations(newValue: boolean): void {
    canCreateConsultations.value = newValue
  }

  const canListPatients = ref(false)
  function setCanListPatients(newValue: boolean): void {
    canListPatients.value = newValue
  }

  const canReceiveConsultations = ref(false)
  function setCanReceiveConsultations(newValue: boolean): void {
    canReceiveConsultations.value = newValue
  }

  const isNotificationAppEnabled = ref(false)
  function setIsNotificationAppEnabled(newValue: boolean): void {
    isNotificationAppEnabled.value = newValue
  }

  const hasAddPatientsPermissions = ref(false)
  function setHasAddPatientsPermissions(newValue: boolean): void {
    hasAddPatientsPermissions.value = newValue
  }

  const isRoutingV2Enabled = ref(false)
  function setRoutingV2Enabled(newValue: boolean): void {
    isRoutingV2Enabled.value = newValue
  }

  const latestEdsAcceptance = ref<PluginContraindicationAcceptanceDto | null>(null)
  function setLatestEdsAcceptance(newValue: PluginContraindicationAcceptanceDto | null): void {
    latestEdsAcceptance.value = newValue
  }

  const latestAkiAcceptance = ref<PluginContraindicationAcceptanceDto | null>(null)
  function setLatestAkiAcceptance(newValue: PluginContraindicationAcceptanceDto | null): void {
    latestAkiAcceptance.value = newValue
  }

  return {
    user,
    getTitle,
    getFullName,
    getDisplayName,
    getMedicalFields,
    getDepartments,
    getHospitals,
    hasSelectedShift,
    hasSelectedRole,
    isIncognito,
    hasTccItRole,
    hasHospitalItRole,
    hasHospitalPhysicianRole,
    isTheSameAsUser,
    hasGodModeShift,
    hasChiefPhysicianShift,
    hasWardWithoutPDMS,
    notificationToken,
    firebaseOptions,
    isConsultationDisabledForPatient,
    setUser,
    setLanguage,
    setCurrentShift,
    setHospitals,
    setWards,
    setMedicalFields,
    setNotificationToken,
    clearNotificationToken,
    setFirebaseOptions,
    notificationTokenLoadingState,
    setNotificationTokenLoadingState,
    setUserRights,
    setCurrentRole,
    hasDoctorRole,
    hadSelectedShift,
    setHadSelectedShift,
    hasSelectedShiftDuration,
    endOfShiftDate,
    setEndOfShiftDate,
    setDepartments,
    setHospitalId,
    hospitalId,
    isInitializing,
    canCreateConsultations,
    setCanCreateConsultations,
    canListPatients,
    setCanListPatients,
    canReceiveConsultations,
    setCanReceiveConsultations,
    isNotificationAppEnabled,
    setIsNotificationAppEnabled,
    hasAddPatientsPermissions,
    setHasAddPatientsPermissions,
    setRoutingV2Enabled,
    isRoutingV2Enabled,
    latestEdsAcceptance,
    setLatestEdsAcceptance,
    latestAkiAcceptance,
    setLatestAkiAcceptance
  }
})
